import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getPalette, COVERS, PROJECT_ACCENTS } from '../themePalette';
import CoverPicker from './CoverPicker';

const API_ROOT = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);

const stableIndex = (str, mod) => {
  let h = 0; const s = String(str || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
};

const coverFor = (project) => {
  if (project.cover_image) {
    return project.cover_image.startsWith('http') ? project.cover_image : `${API_ROOT}${project.cover_image}`;
  }
  return COVERS[stableIndex(project.id || project.name, COVERS.length)];
};

// The one and only project card. Same look on the dashboard and on "View all".
// Page-specific bits are passed in as `actions` (right of the quick-tabs row) and
// `expandedContent` (below it). Drag props are optional.
const ProjectCard = ({
  project, theme = 'dark', items = null, pct = null,
  onDelete, onCoverUpload, onCoverSet, coverBusy = false,
  innerRef, draggableProps, dragHandleProps, isDragging = false,
  actions = null, expandedContent = null,
  onStatusChange = null, onCloseOut = null, onReopen = null,
}) => {
  const navigate = useNavigate();
  const T = getPalette(theme);
  const accent = project.color?.border || PROJECT_ACCENTS[stableIndex(project.id || project.name, PROJECT_ACCENTS.length)];
  const coverSrc = coverFor(project);
  const inactive = project.active === false;
  const open = () => navigate(`/project/${project.id}`);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  // A file's status is now something you can set from anywhere the card shows
  // (dashboard + View all), so it stays consistent everywhere.
  const PROJECT_STATUSES = ['In Progress', 'On Hold', 'Completed'];
  const statusValue = project.status || 'In Progress';
  const STATUS_TONE = { 'In Progress': accent, 'On Hold': T.warning, 'Completed': T.success };
  const statusTone = inactive ? T.muted : (STATUS_TONE[statusValue] || accent);

  return (
    <div ref={innerRef} {...(draggableProps || {})} data-testid={`project-card-${project.id}`}
      className="group rounded-xl overflow-hidden transition-all"
      style={{ background: T.card, borderWidth: '1px', borderStyle: 'solid', borderColor: T.border, borderTopWidth: '3px', borderTopColor: accent, boxShadow: isDragging ? T.shadowHover : T.shadow, ...(draggableProps?.style || {}) }}>
      <div className="relative h-24 w-full bg-cover bg-center" style={{ backgroundImage: `url(${coverSrc})` }}>
        <div onClick={open} className="absolute inset-0 cursor-pointer" />
        {dragHandleProps && (
          <div {...dragHandleProps} data-testid={`drag-card-${project.id}`} title="Drag to reorder" className="absolute left-2 top-2 z-20 grid h-6 w-6 place-items-center rounded-full text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100" style={{ background: 'rgba(10,10,14,0.6)', cursor: 'grab' }}>⋮⋮</div>
        )}
        {onDelete && (
          <button onClick={(e) => onDelete(project, e)} data-testid={`delete-project-${project.id}`} title="Delete project" className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full text-white opacity-0 transition-opacity group-hover:opacity-100" style={{ background: 'rgba(0,0,0,0.5)' }}>×</button>
        )}
        {onCoverUpload && (
          <button onClick={(e) => { e.stopPropagation(); setCoverPickerOpen(true); }} data-testid={`project-cover-change-${project.id}`} title="Set this project's cover — upload or pick a photo already in the app" className="absolute bottom-2 right-2 flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100" style={{ background: 'rgba(10,10,14,0.6)', border: '1px solid rgba(255,255,255,0.25)' }}>
            {coverBusy ? '⏳…' : '📷 Change cover'}
          </button>
        )}
      </div>

      <div className="p-4 cursor-pointer" onClick={open}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: T.muted }}>{items != null ? `${items} Items` : '—'}</div>
        <div className="mt-1 truncate text-[15px] font-semibold" style={{ color: T.text }}>{project.name}</div>
        <div className="truncate text-xs" style={{ color: T.text2 }}>{project.clientName}{project.address ? ` · ${project.address}` : ''}</div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full" style={{ background: T.cardSubtle }}>
          <div className="h-full rounded-full" style={{ width: `${pct || 0}%`, background: accent }} />
        </div>
        <div className="mt-3 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
          {inactive ? (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: `${T.muted}22`, color: T.muted }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: T.muted }} />Closed Out
              </span>
              {onReopen && (
                <button data-testid={`reopen-project-${project.id}`} onClick={(e) => { e.stopPropagation(); onReopen(project, e); }} title="Reopen this file (make active again)" className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: T.goldGrad, color: T.onGold }}>♻ Reopen</button>
              )}
            </div>
          ) : onStatusChange ? (
            <div className="flex items-center gap-1.5">
              <select
                data-testid={`project-status-${project.id}`}
                value={statusValue}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { e.stopPropagation(); onStatusChange(project, e.target.value); }}
                title="Change this file's status"
                className="cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-medium outline-none"
                style={{ background: `${statusTone}22`, color: statusTone, border: `1px solid ${statusTone}66` }}
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s} style={{ background: '#1a1a1a', color: '#fff' }}>{s}</option>
                ))}
              </select>
              {onCloseOut && (
                <button data-testid={`close-out-${project.id}`} onClick={(e) => { e.stopPropagation(); onCloseOut(project, e); }} title="Close out this file (archive — hidden from the dashboard, not deleted)" className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${T.warning}22`, color: T.warning, border: `1px solid ${T.warning}66` }}>🗄 Close out</button>
              )}
            </div>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: `${statusTone}22`, color: statusTone }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusTone }} />{statusValue}
            </span>
          )}
          <span className="text-xs font-semibold" style={{ color: T.text2 }}>{pct != null ? `${pct}%` : ''}</span>
        </div>
      </div>

      <div className="px-4 pb-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={() => navigate(`/project/${project.id}?tab=Walkthrough`)} className="rounded px-2 py-1 text-[10px] font-medium" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>Walk</button>
          <button onClick={() => navigate(`/project/${project.id}?tab=Checklist`)} className="rounded px-2 py-1 text-[10px] font-medium" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>Check</button>
          <button onClick={() => navigate(`/project/${project.id}?tab=FF%26E`)} className="rounded px-2 py-1 text-[10px] font-medium" style={{ background: T.chip, color: T.text2, border: `1px solid ${T.border}` }}>FF&E</button>
          {actions && <div className="ml-auto flex flex-wrap items-center gap-1.5">{actions}</div>}
        </div>
        {expandedContent}
      </div>

      {coverPickerOpen && onCoverUpload && (
        <CoverPicker
          project={project}
          theme={theme}
          busy={coverBusy}
          onClose={() => setCoverPickerOpen(false)}
          onCoverUpload={onCoverUpload}
          onCoverSet={onCoverSet}
        />
      )}
    </div>
  );
};

export { ChevronDown };
export default ProjectCard;
